import React from 'react'
import T from 'prop-types'
import { Box, Grid, Link } from '@mui/material'
import BoltIcon from '@mui/icons-material/Bolt'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import LocalGasStationOutlinedIcon from '@mui/icons-material/LocalGasStationOutlined'
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import itLocale from 'date-fns/locale/it'
import { getFormattedKm, getFormattedNumber } from 'lib/carDetailsLib'
import { COUNTRY_CONFIG_MAP } from 'lib/countryConfig'
import config from 'config'

import CarDetailsList from './CarDetailsList'
import CarDetailsItem from './CarDetailsItem'
import CarDetailsRightColumn from './CarDetailsRightColumn'
import CarDetailsImageWithCarousel from './CarDetailsImageWithCarousel'

export default function CarDetails({
  car,
  price,
  fee,
  center,
  rightBottom,
  readOnly,
  isAssignement,
}) {
  const { t } = useTranslation()

  const countryConfig = COUNTRY_CONFIG_MAP[config.country ?? 'it']

  return (
    <Grid container columnSpacing={1}>
      <Grid
        item
        xs={8}
        sx={{ maxHeight: `calc(100vh - 182px)`, overflowY: 'auto', pr: 1 }}
      >
        <div>
          <Box sx={{ pb: 3 }}>
            <CarDetailsImageWithCarousel car={car} readOnly={readOnly} />
          </Box>
          {center}
          <CarDetailsList title={t('carDetails.specifications')} columns={2}>
            <CarDetailsItem
              title={t('carDetails.km')}
              icon={SpeedOutlinedIcon}
              value={getFormattedKm(car.km)}
            />
            <CarDetailsItem
              title={t('carDetails.fuelType')}
              icon={LocalGasStationOutlinedIcon}
              value={car.fuelType}
            />
            {countryConfig.carDetails.mainFeatures.map((feature) => (
              <CarDetailsItem
                key={feature}
                title={t(`carDetails.${feature}`)}
                icon={DirectionsCarOutlinedIcon}
                value={car[feature]}
              />
            ))}
            <CarDetailsItem
              title={t('carDetails.transmission')}
              icon={AccountTreeOutlinedIcon}
              value={t(`carDetails.mapping.${car.transmission}`)}
            />
            <CarDetailsItem
              title={t('carDetails.monthYear')}
              icon={CalendarTodayOutlinedIcon}
              value={format(new Date(car.date), t('common.date.formatMY'), {
                locale: itLocale,
              })}
            />
            <CarDetailsItem
              title={t('carDetails.powerHorses')}
              icon={BoltIcon}
              value={
                car.enginePowerHp
                  ? `${getFormattedNumber(car.enginePowerHp)} cv`
                  : ''
              }
            />
          </CarDetailsList>

          <CarDetailsList
            title={t('carDetails.mainCharacteristics')}
            columns={2}
          >
            <CarDetailsItem title={t('carDetails.brand')} value={car.brand} />
            <CarDetailsItem
              title={t('carDetails.km')}
              value={getFormattedKm(car.km)}
            />
            <CarDetailsItem title={t('carDetails.model')} value={car.model} />
            <CarDetailsItem
              title={t('carDetails.transmission')}
              value={t(`carDetails.mapping.${car.transmission}`)}
            />
            <CarDetailsItem title={t('carDetails.setup')} value={car.title} />
            <CarDetailsItem
              title={t('carDetails.category')}
              value={t(`carDetails.mapping.${car.vehicleCategory}`)}
            />
            <CarDetailsItem
              title={t('carDetails.euroTaxCode')}
              value={car.euroTaxCode}
            />
            {countryConfig.carDetails.secondaryFeatures.map((feature) => (
              <CarDetailsItem
                key={feature}
                title={t(`carDetails.${feature}`)}
                value={car[feature]}
              />
            ))}
            <CarDetailsItem
              title={t('carDetails.date')}
              value={format(new Date(car.date), t('common.date.formatDMY'), {
                locale: itLocale,
              })}
            />
          </CarDetailsList>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              columnGap: 3,
            }}
          >
            <CarDetailsList title={t('carDetails.motorization')}>
              <CarDetailsItem
                title={t('carDetails.powerHorses')}
                value={
                  car.enginePowerHp
                    ? `${getFormattedNumber(car.enginePowerHp)} cv`
                    : ''
                }
              />
              <CarDetailsItem
                title={t('carDetails.power')}
                value={`${car.enginePowerKw} kw`}
              />
              <CarDetailsItem
                title={t('carDetails.fourWheelDrive')}
                value={car.fourWheelDrive}
              />
            </CarDetailsList>

            <CarDetailsList title={t('carDetails.consuption')}>
              <CarDetailsItem
                title={t('carDetails.co2Emission')}
                value={`${car.co2Emission} g/kg`}
              />
            </CarDetailsList>
          </Box>
          <CarDetailsList title={t('carDetails.carBody')}>
            <CarDetailsItem
              title={t('carDetails.color')}
              value={car.color || '-'}
            />
            {car.linkAppraisal && (
              <Link
                data-testid="car-detail-condition-of-use-report"
                sx={{ cursor: 'pointer' }}
                href={car.linkAppraisal}
                target="_new"
                variant="body2"
              >
                <strong>{t('carDetails.conditionOfUseReport')}</strong>
              </Link>
            )}
          </CarDetailsList>

          <CarDetailsList title={t('carDetails.equipment')} columns={2}>
            {car.equipment?.map((item, i) => (
              <CarDetailsItem key={`${item}_${i}`} value={item} />
            ))}
          </CarDetailsList>
        </div>
      </Grid>
      <Grid item xs={4}>
        <CarDetailsRightColumn
          car={car}
          price={price}
          fee={fee}
          bottom={rightBottom}
          isAssignement={isAssignement}
        />
      </Grid>
    </Grid>
  )
}

CarDetails.propTypes = {
  car: T.object.isRequired,
  price: T.number,
  fee: T.number,
  center: T.node,
  rightBottom: T.node,
  readOnly: T.bool,
  isAssignement: T.bool,
}
